/**
 * Copyright (c) 2026 Zoldier & LCN Softwares All rights reserved.
 *
 * This software is the confidential and proprietary information of Zoldier & LCN Software.
 * You shall not disclose such confidential information and shall use it only
 * in accordance with the terms of the license agreement you entered into with
 * Zoldier & LCN Software.
 */

import enCommon from './en/common.json';
import enLanding from './en/landing.json';
import esCommon from './es/common.json';
import esLanding from './es/landing.json';

/**
 * Locale codes supported by ZIA Platforms Web.
 *
 * Used as the `supportedLngs` option in the i18next configuration to restrict
 * language resolution to known locales and prevent fallback to unsupported ones.
 *
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
export const supportedLngs = ['en', 'es'] as const;

/**
 * Default locale used when no detection is available.
 *
 * Passed to i18next as `fallbackLng`. Any unresolvable language code
 * will cause i18next to serve English translations rather than missing-key placeholders.
 *
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
export const fallbackLng = 'en' as const;

/**
 * Default i18next namespace.
 *
 * When a translation call omits the namespace prefix, i18next resolves keys
 * against this namespace. Shared UI strings (brand name, actions, language
 * toggle) live here so they are reachable without explicit namespace qualification.
 *
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
export const defaultNS = 'common' as const;

/**
 * i18next resources object — locale → namespace → translations.
 *
 * All translation JSON files are bundled statically (no HTTP loader). This
 * guarantees translations are available synchronously at module initialization
 * time, which is required for the SSR-safe rendering path and for tests that
 * do not run a network stack.
 *
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
export const resources = {
  en: {
    common: enCommon,
    landing: enLanding,
  },
  es: {
    common: esCommon,
    landing: esLanding,
  },
} as const;

/**
 * Union type of supported locale codes.
 *
 * Derived from `supportedLngs` so the type and runtime array are always in
 * sync. Use this type for all locale-keyed maps (e.g., `Record<SupportedLocale, SeoMeta>`)
 * and for function parameters that accept a locale argument.
 *
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
export type SupportedLocale = (typeof supportedLngs)[number];
